import { Button, Typography, Box, Stepper, Step, StepLabel, Container, Grid2 as Grid, Link } from '@mui/material';
import { FC, useState, Dispatch, SetStateAction, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { EmployerCreateDto } from '@libs/dto';
import { FormProvider, useForm, useSnackbar } from '@carcareers/contexts';
import { getErrors } from '@carcareers/utils';
import { TextInput, OTPInput, PasswordInput, PhoneInput } from '@carcareers/components';
import {
  useAuthCreateEmployerMutation,
  useAuthCreateExternalOtpMutation,
  useAuthVerifyExternalOtpMutation,
} from '@carcareers/store';

export const CreateEmployer: FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Your Details', 'Verify Your Email', 'Finish Your Account'];

  return (
    <Container maxWidth="md" sx={{ pt: 8 }}>
      <Typography component="h2" variant="body1" align="center" my={3}>
        Looking for a job? Sign up as a jobseeker{' '}
        <Link
          component={RouterLink}
          sx={{
            textDecoration: 'none',
            color: 'primary.main',
            fontWeight: 'bold',
            '&:active': { color: 'primary.dark' },
            '&:hover': { textDecoration: 'underline' },
          }}
          to="/auth/signup/jobseeker"
        >
          here
        </Link>
        .
      </Typography>
      <Typography component="h1" variant="h3" align="center" my={3}>
        Create Your Employer Account
      </Typography>
      <FormProvider>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', p: 4 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 12 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {activeStep === 0 && <BasicInformationStep />}
          {activeStep === 1 && <VerifyOtpStep />}
          {activeStep === 2 && <CreateAccountStep />}
          <StepNav activeStep={activeStep} setActiveStep={setActiveStep} />
        </Box>
      </FormProvider>
    </Container>
  );
};

const BasicInformationStep = () => {
  const { data } = useForm<EmployerCreateDto>();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput required label="First Name" name="user.firstName" autoComplete="given-name" fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput required label="Last Name" name="user.lastName" autoComplete="family-name" fullWidth />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextInput
            required
            label="Email"
            name="user.email"
            type="email"
            autoComplete="email"
            disabled={data?.user?.isEmailVerified}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <PhoneInput required label="Phone Number" name="user.phone" autoComplete="tel" fullWidth />
        </Grid>
      </Grid>
    </Box>
  );
};

const VerifyOtpStep = () => {
  const { data } = useForm<EmployerCreateDto>();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
      <Typography variant="body1" align="center" sx={{ mb: 3 }}>
        We've sent a 6-digit code to {data?.user?.email}.<br />
        Please enter it below to verify your email address.
      </Typography>
      <OTPInput name="otp" length={6} />
    </Box>
  );
};

const CreateAccountStep = () => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput required label="First Name" name="user.firstName" autoComplete="given-name" fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput required label="Last Name" name="user.lastName" autoComplete="family-name" fullWidth />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextInput required label="Email" name="user.email" type="email" autoComplete="email" disabled fullWidth />
        </Grid>
      </Grid>
      <PasswordInput />
    </Box>
  );
};

const StepNav = ({
  activeStep,
  setActiveStep,
}: {
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
}) => {
  const navigate = useNavigate();
  const { toggleSnackbar } = useSnackbar();
  const [isFormValid, setIsFormValid] = useState(false);
  const [loading, setLoading] = useState(false);

  const { data, setData, errors, setErrors } = useForm<EmployerCreateDto>();
  const [createExternalOtp] = useAuthCreateExternalOtpMutation();
  const [verifyExternalOtp] = useAuthVerifyExternalOtpMutation();
  const [createEmployer] = useAuthCreateEmployerMutation();

  const steps = ['Basic Information', 'Verify OTP', 'Create Account'];

  // Define which properties belong to which step for error handling
  const stepErrorProperties = {
    0: ['user.firstName', 'user.lastName', 'user.email', 'user.phone'],
    1: ['otp'],
    2: ['password', 'confirmPassword'],
  };

  // Clear errors for a specific step
  const clearStepErrors = (step: number) => {
    const errorsToRemove = stepErrorProperties[step as keyof typeof stepErrorProperties] || [];
    setErrors(errors.filter((error) => !errorsToRemove.some((property) => error.property.includes(property))));
  };

  useEffect(() => {
    if (activeStep === 0) {
      const isValid = !!(data?.user?.firstName && data?.user?.lastName && data?.user?.email && data?.user?.phone);
      setIsFormValid(isValid);
    } else if (activeStep === 1) {
      const isValid = !!(data?.otp && data.otp.length === 6);
      setIsFormValid(isValid);
    } else if (activeStep === 2) {
      const isValid = !!(data?.password && data?.confirmPassword);
      setIsFormValid(isValid);
    }
  }, [activeStep, data]);

  const handleNext = async () => {
    setLoading(true);

    if (activeStep === 0) {
      // Skip email verification if already verified
      if (data?.user?.isEmailVerified) {
        clearStepErrors(0);
        setActiveStep(2); // Skip directly to step 2 (Create Account)
        setLoading(false);
        return;
      }

      const requestData = {
        email: data?.user?.email.toLowerCase(),
      };

      const response = await createExternalOtp(requestData);

      if (!response.error) {
        clearStepErrors(0);
        setActiveStep((prevStep) => prevStep + 1);
      } else {
        const errors = getErrors(response);
        setErrors(errors);
        toggleSnackbar('error', 'Please check your information and try again.');
      }

      setLoading(false);
    } else if (activeStep === 1) {
      const requestData = {
        email: data?.user?.email.toLowerCase(),
        otp: data?.otp,
      };

      const { error } = await verifyExternalOtp(requestData);

      if (!error) {
        setData('user.isEmailVerified', true);
        clearStepErrors(1);
        setActiveStep((prevStep) => prevStep + 1);
      } else {
        setErrors([{ property: 'otp', errors: ['Invalid OTP. Please try again.'] }]);
        toggleSnackbar('error', 'Invalid OTP. Please try again.');
      }

      setLoading(false);
    } else if (activeStep === 2) {
      const transformedData = {
        ...data,
        user: {
          ...data.user,
          email: data.user.email.toLowerCase(),
        },
      };
      const response = await createEmployer(transformedData);

      if (!response.error) {
        navigate('/auth/login', { state: { isEmployer: true, isSignup: true } });
      } else {
        const errors = getErrors(response);
        setErrors(errors);

        // Determine which step contains the errors and navigate accordingly
        let targetStep = 2; // Default to current step
        let errorMessage = 'Failed to create an account. Please try again.';

        // Check for step 0 errors (basic information)
        const hasStep0Errors = errors.some((error) => stepErrorProperties[0].includes(error.property));

        // Check for step 2 errors (password fields)
        const hasStep2Errors = errors.some((error) => stepErrorProperties[2].includes(error.property));

        if (hasStep0Errors) {
          targetStep = 0;
          errorMessage = 'Please check your basic information and try again.';
        } else if (hasStep2Errors) {
          targetStep = 2;
          errorMessage = 'Please check your password and try again.';
        }

        setActiveStep(targetStep);
        toggleSnackbar('error', errorMessage);
      }

      setLoading(false);
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate(-1);
    } else if (activeStep === 2 && data?.user?.isEmailVerified) {
      // Skip back to step 0 if email is verified (skip step 1)
      clearStepErrors(activeStep);
      setActiveStep(0);
    } else {
      clearStepErrors(activeStep);
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 12 }}>
      <Button size="large" variant="outlined" color="secondary" onClick={handleBack}>
        {activeStep === 0 ? 'Cancel' : 'Back'}
      </Button>

      {activeStep <= steps.length - 1 && (
        <Button
          size="large"
          variant="contained"
          color="primary"
          onClick={handleNext}
          disabled={!isFormValid}
          loading={loading}
        >
          {activeStep === 2 ? 'Create Account' : 'Next'}
        </Button>
      )}
    </Box>
  );
};

export default CreateEmployer;
